import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Clock, CreditCard, Shield, ArrowLeft, Users, Gift, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import Navbar from '@/components/Navbar';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingPage() {
  const { serviceId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  
  const [service, setService] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState('basic');
  const [reqDescription, setReqDescription] = useState('');
  const [reqBudget, setReqBudget] = useState('');
  const [reqTimeline, setReqTimeline] = useState('');
  const [reqDeliverables, setReqDeliverables] = useState('');
  const [reqReferences, setReqReferences] = useState('');
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  
  // Referral state
  const [showReferralPopup, setShowReferralPopup] = useState(false);
  const [hasReferral, setHasReferral] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [validatingCode, setValidatingCode] = useState(false);
  const [referrerName, setReferrerName] = useState(null);
  const [bookingId, setBookingId] = useState(null);

  useEffect(() => {
    fetchService();
    
    // Check for payment status
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
    
    // Check for referral code in URL
    const refCode = searchParams.get('ref');
    if (refCode) {
      setPromoCode(refCode);
    }

    // Pre-fill the project brief from a prior booking ("Order this again")
    const reorderId = searchParams.get('reorder');
    if (reorderId && user) {
      fetch(`${API}/vendors/reorder/${reorderId}`, { headers: getAuthHeaders() })
        .then((r) => r.ok ? r.json() : null)
        .then((d) => {
          if (!d) return;
          if (d.package_name) setSelectedPackage(d.package_name);
          const det = d.requirements_detail || {};
          setReqDescription(det.description || d.requirements || '');
          setReqBudget(det.budget || '');
          setReqTimeline(det.timeline || '');
          setReqDeliverables(det.deliverables || '');
          setReqReferences(det.references || '');
          toast.success('We pre-filled your brief from last time — edit anything before you book.');
        })
        .catch(() => {});
    }
  }, [serviceId, searchParams, user]);

  const fetchService = async () => {
    try {
      const response = await fetch(`${API}/services/${serviceId}/`);
      if (response.ok) {
        const data = await response.json();
        setService(data);
        if (data.packages?.length > 0) {
          setSelectedPackage(data.packages[0].name);
        }
      } else {
        navigate('/browse');
      }
    } catch (error) {
      console.error('Failed to fetch service:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    // Poll for payment status
    const pollStatus = async (attempts = 0) => {
      if (attempts >= 5) return;
      try {
        const response = await fetch(`${API}/escrow/status/${sessionId}`, {
          headers: getAuthHeaders(),
        });
        if (response.ok) {
          const data = await response.json();
          if (data.payment_status === 'paid') {
            const paymentType = searchParams.get('payment');
            if (paymentType === 'completion') {
              toast.success('Final payment complete! The job is now fully paid.');
            } else {
              toast.success('Down payment successful! Your booking is confirmed. Vendor will begin work.');
            }
            return;
          } else if (data.status === 'expired') {
            toast.error('Payment session expired. Please try again.');
            return;
          }
          // Continue polling
          setTimeout(() => pollStatus(attempts + 1), 2000);
        }
      } catch (error) {
        console.error('Failed to check payment status:', error);
      }
    };
    pollStatus();
  };

  const validatePromoCode = async () => {
    if (!promoCode.trim()) {
      toast.error('Please enter a promo code');
      return;
    }
    
    setValidatingCode(true);
    try {
      const response = await fetch(`${API}/supporters/validate-promo-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ promo_code: promoCode.trim() })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setReferrerName(data.referrer_name);
        toast.success(`Code valid! ${data.referrer_name} will earn a referral reward.`);
      } else {
        throw new Error(data.detail || 'Invalid promo code');
      }
    } catch (error) {
      toast.error(error.message);
      setReferrerName(null);
    } finally {
      setValidatingCode(false);
    }
  };

  const applyReferralToBooking = async (bookingId) => {
    if (!promoCode.trim() || !referrerName) return;
    
    try {
      await fetch(`${API}/supporters/apply-referral`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ 
          promo_code: promoCode.trim(),
          booking_id: bookingId
        })
      });
    } catch (error) {
      console.error('Failed to apply referral:', error);
    }
  };

  const handleInitiateBooking = async () => {
    if (!user) {
      navigate('/auth');
      return;
    }
    // Validate required fields of the project brief
    if (reqDescription.trim().length < 20) {
      toast.error('Please describe your project in more detail (min 20 characters)');
      return;
    }
    if (reqDeliverables.trim().length < 4) {
      toast.error('Please list what you need delivered');
      return;
    }

    // Show referral popup first
    setShowReferralPopup(true);
  };

  const handleBooking = async () => {
    setShowReferralPopup(false);
    setBooking(true);
    
    try {
      const summary = `Project: ${reqDescription.trim()}\nBudget: ${reqBudget.trim() || 'Not specified'}\nTimeline: ${reqTimeline.trim() || 'Not specified'}\nDeliverables: ${reqDeliverables.trim()}\nReferences: ${reqReferences.trim() || 'None'}`;
      // Create booking
      const bookingResponse = await fetch(`${API}/bookings/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          service_id: serviceId,
          package_name: selectedPackage,
          requirements: summary,
          requirements_detail: {
            description: reqDescription.trim(),
            budget: reqBudget.trim(),
            timeline: reqTimeline.trim(),
            deliverables: reqDeliverables.trim(),
            references: reqReferences.trim(),
          },
        })
      });

      if (!bookingResponse.ok) {
        throw new Error('Failed to create booking');
      }

      const bookingData = await bookingResponse.json();
      setBookingId(bookingData.booking_id);

      // Apply referral if valid
      if (hasReferral && promoCode && referrerName) {
        await applyReferralToBooking(bookingData.booking_id);
      }

      // Create escrow payment (50% down)
      const paymentResponse = await fetch(`${API}/escrow/create-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          booking_id: bookingData.booking_id,
          origin_url: window.location.origin
        })
      });

      if (!paymentResponse.ok) {
        const errData = await paymentResponse.json().catch(() => ({}));
        throw new Error(errData.detail || 'Failed to create payment');
      }

      const paymentData = await paymentResponse.json();
      
      // Redirect to Stripe Checkout
      window.location.href = paymentData.checkout_url;
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBooking(false);
    }
  };

  const handleReferralChoice = (choice) => {
    setHasReferral(choice);
    if (!choice) {
      // User said No, proceed to booking
      handleBooking();
    }
  };

  const currentPackage = service?.packages?.find(p => p.name === selectedPackage);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-950">
        <Navbar />
        <div className="pt-24 container-main">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-stone-800 rounded w-1/3"></div>
            <div className="h-64 bg-stone-800 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-950">
      <Navbar />

      {/* Referral Popup */}
      <AnimatePresence>
        {showReferralPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            onClick={() => setShowReferralPopup(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-stone-900 rounded-2xl p-6 max-w-md w-full border border-stone-800"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Gift className="w-8 h-8 text-yellow-400" />
                  <h3 className="font-syne text-xl font-bold text-white">Referral Reward</h3>
                </div>
                <button
                  onClick={() => setShowReferralPopup(false)}
                  className="text-stone-500 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {hasReferral === null ? (
                <div className="space-y-4">
                  <p className="text-stone-300 font-outfit text-center">
                    Did anyone refer you to this Indigenous business?
                  </p>
                  <div className="flex gap-3">
                    <Button
                      onClick={() => handleReferralChoice(true)}
                      className="flex-1 bg-yellow-400 hover:bg-yellow-300 text-black font-bold"
                      data-testid="referral-yes"
                    >
                      Yes
                    </Button>
                    <Button
                      onClick={() => handleReferralChoice(false)}
                      variant="outline"
                      className="flex-1 border-stone-700 text-stone-300 hover:bg-stone-800"
                      data-testid="referral-no"
                    >
                      No
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-stone-300 font-outfit">
                    Enter their promo code to give them a referral reward!
                  </p>
                  
                  <div className="flex gap-2">
                    <Input
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      placeholder="Enter promo code"
                      className="input-field flex-1 uppercase tracking-widest font-mono text-lg"
                      maxLength={8}
                      data-testid="promo-code-input"
                    />
                    <Button
                      onClick={validatePromoCode}
                      disabled={validatingCode}
                      className="bg-stone-800 hover:bg-stone-700 text-white"
                    >
                      {validatingCode ? '...' : 'Verify'}
                    </Button>
                  </div>
                  
                  {referrerName && (
                    <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/30">
                      <p className="text-green-400 font-outfit text-sm flex items-center gap-2">
                        <Check className="w-4 h-4" />
                        Code valid! <span className="font-bold">{referrerName}</span> will earn a reward.
                      </p>
                    </div>
                  )}
                  
                  <div className="flex gap-3 pt-2">
                    <Button
                      onClick={handleBooking}
                      disabled={booking}
                      className="flex-1 bg-yellow-400 hover:bg-yellow-300 text-black font-bold"
                      data-testid="proceed-booking"
                    >
                      {booking ? 'Processing...' : 'Continue to Payment'}
                    </Button>
                    <Button
                      onClick={() => {
                        setHasReferral(null);
                        setPromoCode('');
                        setReferrerName(null);
                      }}
                      variant="ghost"
                      className="text-stone-400"
                    >
                      Back
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <main className="pt-24 pb-16">
        <div className="container-main max-w-4xl">
          {/* Back Button */}
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-stone-400 hover:text-stone-200 mb-8 font-outfit"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-3 space-y-6">
              <motion.div 
                className="card-vendor"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <h1 className="font-syne text-2xl font-bold text-stone-100 mb-2" data-testid="booking-title">
                  Book Service
                </h1>
                <h2 className="text-stone-400 font-outfit mb-6">{service?.title}</h2>

                {/* Vendor Info */}
                {service?.vendor && (
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-stone-800/50 mb-6">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-stone-700">
                      {service.vendor.profile_image ? (
                        <img src={service.vendor.profile_image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Users className="w-6 h-6 text-stone-500" />
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-stone-200 font-outfit font-medium">{service.vendor.business_name || service.vendor.owner_name}</p>
                      <p className="text-stone-500 text-sm">{service.vendor.location}</p>
                    </div>
                  </div>
                )}

                {/* Package Selection */}
                <div className="mb-6">
                  <Label className="text-stone-300 font-outfit mb-3 block">Select Package</Label>
                  <RadioGroup value={selectedPackage} onValueChange={setSelectedPackage} className="space-y-3">
                    {service?.packages?.map((pkg) => (
                      <div key={pkg.name}>
                        <RadioGroupItem value={pkg.name} id={pkg.name} className="peer sr-only" />
                        <Label 
                          htmlFor={pkg.name}
                          className="flex items-start justify-between p-4 rounded-xl border border-stone-800 cursor-pointer peer-data-[state=checked]:border-amber-500 peer-data-[state=checked]:bg-amber-500/10 hover:border-stone-700 transition-colors"
                          data-testid={`package-${pkg.name}`}
                        >
                          <div>
                            <span className="text-stone-200 font-outfit font-medium capitalize block">{pkg.title || pkg.name}</span>
                            <span className="text-stone-500 text-sm">{pkg.description}</span>
                            <div className="flex items-center gap-2 mt-2 text-xs text-stone-500">
                              <Clock className="w-3 h-3" />
                              <span>{pkg.delivery_days} days delivery</span>
                            </div>
                          </div>
                          <span className="text-amber-500 font-mono font-bold text-lg">${pkg.price}</span>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                {/* Project Requirements — Consumer Brief */}
                <div className="space-y-4" data-testid="project-requirements-form">
                  <div className="flex items-center gap-2 pb-2 border-b border-stone-800">
                    <h3 className="font-syne text-base font-bold text-stone-100">Project Brief</h3>
                    <span className="text-xs text-amber-400">Required before booking</span>
                  </div>
                  <p className="text-stone-500 text-xs -mt-2">
                    Vendors deliver faster when they have context. Tell them about the project before you pay.
                  </p>

                  <div>
                    <Label className="text-stone-300 font-outfit mb-1 block text-sm">Project description *</Label>
                    <Textarea
                      value={reqDescription}
                      onChange={(e) => setReqDescription(e.target.value)}
                      placeholder="What are you trying to accomplish? Audience, purpose, style, tone, must-haves…"
                      className="input-field w-full min-h-[100px]"
                      data-testid="req-description"
                    />
                    <p className="text-stone-500 text-[10px] mt-1">{reqDescription.length} chars · min 20</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-stone-300 font-outfit mb-1 block text-sm">Budget (optional)</Label>
                      <Input
                        value={reqBudget}
                        onChange={(e) => setReqBudget(e.target.value)}
                        placeholder="e.g., $200-$400 flexible"
                        className="input-field w-full"
                        data-testid="req-budget"
                      />
                    </div>
                    <div>
                      <Label className="text-stone-300 font-outfit mb-1 block text-sm">Timeline / Deadline (optional)</Label>
                      <Input
                        value={reqTimeline}
                        onChange={(e) => setReqTimeline(e.target.value)}
                        placeholder="e.g., Need it by March 15"
                        className="input-field w-full"
                        data-testid="req-timeline"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-stone-300 font-outfit mb-1 block text-sm">What should be delivered? *</Label>
                    <Textarea
                      value={reqDeliverables}
                      onChange={(e) => setReqDeliverables(e.target.value)}
                      placeholder="e.g., 3 logo concepts in SVG + PNG, 2 revision rounds, source files"
                      className="input-field w-full min-h-[80px]"
                      data-testid="req-deliverables"
                    />
                  </div>

                  <div>
                    <Label className="text-stone-300 font-outfit mb-1 block text-sm">References / inspiration (optional)</Label>
                    <Textarea
                      value={reqReferences}
                      onChange={(e) => setReqReferences(e.target.value)}
                      placeholder="Links, competitor sites, brands you admire, or style notes"
                      className="input-field w-full min-h-[60px]"
                      data-testid="req-references"
                    />
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-2">
              <motion.div 
                className="card-vendor sticky top-24"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <h3 className="font-syne text-lg font-bold text-stone-100 mb-4">Order Summary</h3>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-stone-400 font-outfit capitalize">{selectedPackage} Package</span>
                    <span className="text-stone-200 font-mono">${currentPackage?.price || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-stone-400 font-outfit">Delivery</span>
                    <span className="text-stone-200 font-mono">{currentPackage?.delivery_days || 0} days</span>
                  </div>
                </div>

                <div className="border-t border-stone-800 pt-4 mb-6">
                  <div className="flex items-center justify-between">
                    <span className="text-stone-200 font-outfit font-medium">Total</span>
                    <span className="text-amber-500 font-mono font-bold text-xl">${currentPackage?.price || 0}</span>
                  </div>
                </div>

                {/* Escrow Info */}
                <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 mb-6">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-amber-400 font-outfit font-medium text-sm">Escrow Protection</p>
                      <p className="text-stone-400 text-xs mt-1">
                        Your payment is held securely in escrow. Pay 50% now to start, remaining 50% on completion.
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-stone-400">Down payment (50%)</span>
                      <span className="text-amber-400 font-mono font-bold">${((currentPackage?.price || 0) * 0.50).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-stone-400">On completion (50%)</span>
                      <span className="text-stone-500 font-mono">${((currentPackage?.price || 0) * 0.50).toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={handleInitiateBooking}
                  disabled={booking}
                  className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold"
                  data-testid="confirm-booking"
                >
                  {booking ? 'Processing...' : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Pay ${((currentPackage?.price || 0) * 0.50).toFixed(2)} Down Payment
                    </>
                  )}
                </Button>

                <p className="text-stone-500 text-xs text-center mt-4 font-outfit">
                  By booking, you agree to our Terms of Service and Escrow Policy
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
